import { hasTimeScheduled, isEquipmentBased, isScheduled } from "common";
import { isNil, sumBy } from "lodash";
import { AllowedPrivacy, BaseScheduleType, Experience } from "types";
import { hasOrganizerOnlyQuestions } from "../../../models/Form/Form";
import { isOrganizerSelected } from "../../../models/Guest";
import { CreatePurchaseItemState, useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { getArrivalErrorMessages, hasSomeEmptyRequiredFields } from "../helpers/PurchaseCreateValidationHelpers";
import { getCreatePurchasePackageFields } from "./useCreatePurchasePackageFields";
import { getMaxAttendees, hasSecondaryDemographics, hasUniqueGuestIdentification } from "@/models/Experience";

export const getExperienceValidations = () => {
    const { showNextButton } = getCreatePurchasePackageFields();

    const experience = useCreatePurchaseStore.getState().getActiveProduct<Experience>();
    const { purchase } = useCreatePurchaseStore.getState();
    const item = useCreatePurchaseStore.getState().getActiveItem();
    const { timeslot } = item;

    const { errors } = useCreatePurchaseStore.getState();

    const isOrganizerOnly = !hasUniqueGuestIdentification(experience);

    const getDisabledReasons = () => {
        if (!experience?.schedules) {
            return [];
        }

        const messages = [];

        messages.push(
            ...getTimeslotValidationErrors(experience, item),
            ...getQuestionnaireErrors(experience, item, isOrganizerOnly),
        );

        if (isEquipmentBased(experience)) {
            messages.push(...getEquipmentAvailabilityErrors(item), ...getAttendeeErrors(experience, item));
        }

        if (timeslot && timeslot?.capacity.open < item.quantity) {
            messages.push("Quantity exceeds available capacity");
        }

        if (experience?.group && item.quantity < experience.group?.orderMin) {
            messages.push("Quantity less than the minimum capacity allowed");
        }

        // Main Guest validation is shown when there are more than 1 guests and there are organizer questions in questionnaire
        if (
            (item.guests?.length ?? 1) > 1 &&
            hasOrganizerOnlyQuestions(item.form) &&
            !isOrganizerSelected(item.guests)
        ) {
            messages.push("Please select the main guest from the guest info section");
        }

        messages.push(...getDuplicatePrivateBookingErrors(item, purchase));

        // show error only when user is on the last product page for package checkout
        if (!showNextButton) {
            messages.push(...getArrivalErrorMessages(errors ?? []));
        }

        return messages;
    };

    return { getDisabledReasons };
};

const getQuestionnaireErrors = (experience: Experience, item: CreatePurchaseItemState, isOrganizerOnly: boolean) => {
    // Equipment with no attendees has no one to answer a guest questionnaire.
    const isEquipmentWithNoGuests = isEquipmentBased(experience) && (item.guests?.length ?? 0) === 0;
    if (isEquipmentWithNoGuests || item.disableRequiredFormFields) {
        return [];
    }

    return hasSomeEmptyRequiredFields(item, isOrganizerOnly)
        ? ['Please complete the Guest Info questionnaire or toggle on "Ignore questionnaire validation"']
        : [];
};

const getDuplicatePrivateBookingErrors = (item: CreatePurchaseItemState, purchase?: { items: unknown[] }) => {
    if (!purchase) {
        return [];
    }

    const hasDuplicatePrivateBooking = (purchase.items as CreatePurchaseItemState[]).some((purchaseItem) => {
        if (!("arrivalDateTime" in purchaseItem) || purchaseItem.id === item.id) {
            return false;
        }

        return (
            item.product.id === purchaseItem.product.id &&
            purchaseItem.privacy === AllowedPrivacy.PRIVATE &&
            item.privacy === AllowedPrivacy.PRIVATE &&
            purchaseItem.arrivalDateTime?.isSame(item.arrivalDateTime)
        );
    });

    return hasDuplicatePrivateBooking
        ? [
              "Only one reservation can be made at a time for the exact same event. (You already have a reservation in your cart for this exact event)",
          ]
        : [];
};

export const getTimeslotValidationErrors = (experience: Experience, item: CreatePurchaseItemState) => {
    const error = "Timeslot is not selected";

    // Disable is no arrival date or arrival time
    const requiresTimeslot = experience.schedules.some((schedule) => {
        return !!schedule.times?.length && schedule.type !== BaseScheduleType.UNAVAILABLE;
    });

    const hasArrivalDateScheduled = item.arrivalDate;
    const hasArrivalTimeScheduled = !isNil(item.arrivalTime);

    const isExperienceScheduled = isScheduled(experience);

    const hasItems = isEquipmentBased(experience)
        ? (item.equipmentLineItems ?? []).some((li) => li.quantity > 0)
        : item.demographicLineItems.length > 0;

    if (!hasItems || (isExperienceScheduled && requiresTimeslot && !item.arrivalDate)) {
        return [error];
    }

    // It has Time Scheduled with Date and no Time
    if (
        isExperienceScheduled &&
        hasTimeScheduled(experience) &&
        hasArrivalDateScheduled &&
        requiresTimeslot &&
        !hasArrivalTimeScheduled
    ) {
        return [error];
    }

    // No Time Scheduled with no Date
    if (isExperienceScheduled && !hasArrivalDateScheduled) {
        return [error];
    }

    return [];
};

export const getEquipmentAvailabilityErrors = (item: CreatePurchaseItemState) => {
    const units = item.timeslot?.units;
    if (!units) {
        return [];
    }

    return (item.equipmentLineItems ?? []).flatMap((lineItem) => {
        if (lineItem.quantity <= 0) {
            return [];
        }

        const count = units.find((unit) => unit.template?.id === lineItem.template?.id)?.count;
        if (count === undefined || lineItem.quantity <= count) {
            return [];
        }

        return [count === 0 ? `${lineItem.name} is unavailable` : `${lineItem.name} quantity cannot exceed ${count}`];
    });
};

export const getAttendeeErrors = (experience: Experience, item: CreatePurchaseItemState) => {
    if (!hasSecondaryDemographics(experience)) {
        return [];
    }

    const totalAttendees = sumBy(item.attendeeLineItems ?? [], (lineItem) => lineItem.quantity);
    if (totalAttendees < 1) {
        return ["Quantity must be at least 1"];
    }

    const maxAttendees = getMaxAttendees(experience, item.equipmentLineItems ?? []);

    return maxAttendees > 0 && totalAttendees > maxAttendees ? [`Attendees cannot exceed ${maxAttendees}`] : [];
};
